/**
 * 거래처 360 — 거래처 마스터 행(이름) 클릭 시 (v2.318.0 전 이름 「거래처 상세 통합뷰」).
 *
 * 사업자정보(번호·주소·비고) + 이 거래처와 연결된 모든 것을 한 창에: 맨 위 하이라이트 4칸(올해 발행 매출 · 진행 중 영업건 ·
 * 진행 중 유지보수 계약 · 마지막 접촉) → 건수 카드 → 영업 · 매입 · 유지보수 · 담당자 · 견적 + **v2.318.0** 딜등록 · 납품 일정 ·
 * 메모 · 명절선물 · 보낸 메일 · 첨부 문서(Salesforce Account 페이지의 Related 목록 · 기능·메뉴 검토 F1 · 사용자 「추천안대로」).
 * 매칭은 백엔드 norm_company() 정규화로 표기차(㈜/(주)/주식회사·공백)를 흡수. 영업 계정도 읽는다(v2.318.0).
 * GET /admin/partner-companies/:id/detail.
 */

import { useQuery } from '@tanstack/react-query';
import { listContacts, contactPhones } from '../lib/partner-contact-api';
import { getPartnerCompanyDetail } from '../lib/admin-api';
import type { PartnerKind } from '../lib/admin-api';
import { DraggableModal } from './DraggableModal';
import { LoadingLines } from './ui/Skeleton';
import { previewAttachment } from '../lib/attachment-api';
import { RecentRecorder } from './RecentRecorder';

const KIND_LABEL: Record<PartnerKind, string> = { supplier: '매입처', customer: '고객사', both: '양쪽' };
const KIND_BADGE: Record<PartnerKind, string> = {
  supplier: 'bg-sky-100 text-sky-700',
  customer: 'bg-amber-100 text-amber-700',
  both: 'bg-violet-100 text-violet-700',
};

function won(n: number | string | null): string {
  // 서버가 numeric 을 글자('3800000.00')로 주는 칸이 있어 숫자로 바꿔 찍는다(v2.318.0 전에는 그대로 나와 소수점 둘이 붙었다)
  if (n == null || n === '') return '-';
  const v = Number(n);
  return Number.isFinite(v) ? Math.round(v).toLocaleString('ko-KR') : String(n);
}
/** 시각 글자 → 「2026-10-31」 · 오늘 기준 며칠 전인지 */
function agoLabel(iso: string | null): string {
  if (!iso) return '기록 없음';
  const d = iso.slice(0, 10);
  const days = Math.floor((Date.now() - new Date(d + 'T00:00:00').getTime()) / 86_400_000);
  if (!Number.isFinite(days)) return d;
  return days <= 0 ? `${d} (오늘)` : `${d} (${days}일 전)`;
}
/** 억·만 단위 짧은 금액 — 하이라이트 칸용 */
function wonShort(n: number): string {
  if (n >= 100_000_000) return `${(n / 100_000_000).toFixed(n % 100_000_000 === 0 ? 0 : 1)}억`;
  if (n >= 10_000) return `${Math.round(n / 10_000).toLocaleString('ko-KR')}만`;
  return n.toLocaleString('ko-KR');
}
function dash(s: string | null | undefined): string {
  return s && s.trim() ? s : '-';
}

export function PartnerDetailModal({ id, onClose }: { id: number; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['admin', 'partnerCompanyDetail', id],
    queryFn: () => getPartnerCompanyDetail(id),
    staleTime: 30_000,
  });
  const d = q.data;
  // 이 거래처의 담당자 (v2.143.0 · 거래처 담당자 주소록) — 안 쓰는 사람도 함께 보인다
  const contactsQ = useQuery({
    queryKey: ['partnerContactsOf', id],
    queryFn: () => listContacts({ companyId: id, inactive: true }),
    staleTime: 30_000,
  });
  const contactRows = contactsQ.data?.rows ?? [];

  return (
    <DraggableModal
      onClose={onClose}
      initialWidth={1024}
      closeOnBackdrop={false}
      closeOnEsc /* 보기 창 (v2.231.0 · H-4) */
      bodyClassName="flex-1 min-h-0 overflow-hidden flex flex-col"
    >
        {/* 헤더 */}
        <div className="dm-drag-handle cursor-move flex items-start justify-between p-5 border-b border-slate-200">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="ui-section-title">{d?.partner.name ?? '…'}</h2>
              {d && <RecentRecorder kind="partner" refId={String(d.partner.id)} label={d.partner.name} path={`/partners?open=${d.partner.id}`} />}
              {d && (
                <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${KIND_BADGE[d.partner.kind]}`}>
                  {KIND_LABEL[d.partner.kind]}
                </span>
              )}
            </div>
            {d && (
              <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                <span>사업자번호 <span className="font-mono text-slate-700">{dash(d.partner.businessNumber)}</span></span>
                {d.partner.note && <span>비고 <span className="text-slate-700">{d.partner.note}</span></span>}
              </div>
            )}
            {d?.partner.address && <div className="text-xs text-slate-500 mt-0.5 truncate">{d.partner.address}</div>}
          </div>
          <button onClick={onClose} className="ui-close ml-4">×</button>
        </div>

        {/* 본문 */}
        <div className="p-5 space-y-6 flex-1 min-h-0 overflow-y-auto">
          {q.isLoading && <div className="text-center text-slate-400 py-10"><LoadingLines /></div>}
          {q.isError && <div className="text-center text-red-500 py-10">오류: {(q.error as Error).message}</div>}
          {d && (
            <>
              {/* 하이라이트 4칸 (v2.318.0 · Salesforce 하이라이트 패널 — 7칸 이하) */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Highlight label="올해 발행 매출" value={d.highlights.yearSales > 0 ? wonShort(d.highlights.yearSales) : '0'} sub={`${d.highlights.yearCount}건 · VAT 별도`} />
                <Highlight label="진행 중 영업건" value={`${d.highlights.openCount}건`} sub={d.highlights.openAmount > 0 ? `제안가 ${wonShort(d.highlights.openAmount)}` : '일반·유력·발주·납품(미발행)'} />
                <Highlight label="유지보수 계약(진행)" value={`${d.highlights.maintOpen}건`} sub="확정 · 종료일 전" />
                <Highlight label="마지막 접촉" value={d.highlights.lastTouch ? agoLabel(d.highlights.lastTouch).replace(/^\S+ /, '').replace(/[()]/g, '') : '기록 없음'} sub={d.highlights.lastTouch ? `${d.highlights.lastTouch.slice(0, 10)} · 영업건 변경·메모·보낸 메일 중 최근` : '영업건 변경 · 메모 · 보낸 메일 기준'} />
              </div>

              {/* 요약 카드 */}
              <div className="grid grid-cols-4 gap-3">
                <SummaryCard label="영업" n={d.sales.total} cls="text-blue-700 bg-blue-50 border-blue-200" />
                <SummaryCard label="매입" n={d.purchase.total} cls="text-emerald-700 bg-emerald-50 border-emerald-200" />
                <SummaryCard label="유지보수" n={d.maintenance.total} cls="text-violet-700 bg-violet-50 border-violet-200" />
                <SummaryCard label="견적" n={d.quote.total} cls="text-amber-700 bg-amber-50 border-amber-200" />
              </div>

              <Section title="영업" total={d.sales.total}>
                <Table head={['매출번호', '제안업체', '엔드유저', '프로젝트', '제안가', '상태', '계약일']}>
                  {d.sales.rows.map((r) => (
                    <tr key={r.salesId} className="border-t border-slate-100">
                      <Td mono>{r.salesId}</Td>
                      <Td>{dash(r.proposalCompany)}</Td>
                      <Td>{dash(r.endUser)}</Td>
                      <Td>{dash(r.projectName)}</Td>
                      <Td right>{won(r.proposalPrice)}</Td>
                      <Td>{dash(r.status)}</Td>
                      <Td>{dash(r.contractDate)}</Td>
                    </tr>
                  ))}
                </Table>
              </Section>

              <Section title="매입" total={d.purchase.total}>
                <Table head={['매입번호', '매입처', '엔드유저', '품목', '금액', '발주일']}>
                  {d.purchase.rows.map((r) => (
                    <tr key={r.purchaseId} className="border-t border-slate-100">
                      <Td mono>{r.purchaseId}</Td>
                      <Td>{dash(r.supplier)}</Td>
                      <Td>{dash(r.endUser)}</Td>
                      <Td>{dash(r.itemGroup || r.description)}</Td>
                      <Td right>{won(r.amount)}</Td>
                      <Td>{dash(r.orderDate)}</Td>
                    </tr>
                  ))}
                </Table>
              </Section>

              <Section title="유지보수" total={d.maintenance.total}>
                <Table head={['계약명', '엔드유저', '청구업체', '낙찰업체', '상태', '계약기간']}>
                  {d.maintenance.rows.map((r) => (
                    <tr key={r.contractId} className="border-t border-slate-100">
                      <Td>{dash(r.contractName)}</Td>
                      <Td>{dash(r.endUser)}</Td>
                      <Td>{dash(r.contractCompany)}</Td>
                      <Td>{dash(r.awardCompany)}</Td>
                      <Td>{dash(r.status)}</Td>
                      <Td>{r.contractStart || '?'} ~ {r.contractEnd || '?'}</Td>
                    </tr>
                  ))}
                </Table>
              </Section>

              <Section title="담당자" total={contactRows.length}>
                <Table head={['이름', '직급', '부서', '이메일', '연락처', '메모', '사용']}>
                  {contactRows.map((c) => (
                    <tr key={c.id} className={`border-t border-slate-100 ${c.isActive ? '' : 'text-slate-400'}`}>
                      <Td>{dash(c.name)}</Td>
                      <Td>{dash(c.title)}</Td>
                      <Td>{dash(c.department)}</Td>
                      <Td>{dash(c.email)}</Td>
                      <Td>{dash(contactPhones(c))}</Td>
                      <Td>{dash(c.note)}</Td>
                      <Td>{c.isActive ? '사용' : '안 씀'}</Td>
                    </tr>
                  ))}
                </Table>
              </Section>

              <Section title="견적" total={d.quote.total}>
                <Table head={['견적번호', '수신처', '프로젝트', '금액(VAT포함)', '견적일']}>
                  {d.quote.rows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <Td mono>{dash(r.quoteNo)}</Td>
                      <Td>{dash(r.recipientCompany)}</Td>
                      <Td>{dash(r.projectName)}</Td>
                      <Td right>{won(r.totalWithVat)}</Td>
                      <Td>{dash(r.quoteDate)}</Td>
                    </tr>
                  ))}
                </Table>
              </Section>

              {/* ── v2.318.0 관련 목록 보강 ── */}
              <Section title="딜등록" total={d.deal.total}>
                <Table head={['벤더', '접수번호', '프로젝트', '엔드유저', '제안사', '상태', '만료일', 'D-day', '담당', '영업건']}>
                  {d.deal.rows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <Td>{dash(r.vendor)}</Td>
                      <Td mono>{dash(r.registrationNo)}</Td>
                      <Td>{dash(r.project)}</Td>
                      <Td>{dash(r.endUser)}</Td>
                      <Td>{dash(r.tier2)}</Td>
                      <Td>{dash(r.status)}</Td>
                      <Td>{dash(r.expireDate)}</Td>
                      <Td right>{r.dday == null ? '-' : r.dday >= 0 ? `D-${r.dday}` : `D+${-r.dday}`}</Td>
                      <Td>{dash(r.ownerName)}</Td>
                      <Td mono>{dash(r.salesId)}</Td>
                    </tr>
                  ))}
                </Table>
              </Section>

              <Section title="납품 일정" total={d.delivery.total}>
                <Table head={['납품예정일', '시간', '매출번호', '프로젝트', '엔드유저', '납품장소', '상태', '납품완료일']}>
                  {d.delivery.rows.map((r) => (
                    <tr key={r.salesId} className="border-t border-slate-100">
                      <Td>{dash(r.expectedDeliveryDate)}</Td>
                      <Td>{dash(r.startTime)}</Td>
                      <Td mono>{r.salesId}</Td>
                      <Td>{dash(r.projectName)}</Td>
                      <Td>{dash(r.endUser)}</Td>
                      <Td>{dash(r.deliveryPlace)}</Td>
                      <Td>{dash(r.status)}</Td>
                      <Td>{dash(r.deliveryDate)}</Td>
                    </tr>
                  ))}
                </Table>
              </Section>

              <Section title="메모" total={d.memo.total}>
                <Table head={['날짜', '쓴 사람', '매출번호', '프로젝트', '내용']}>
                  {d.memo.rows.map((r, i) => (
                    <tr key={`${r.salesId}-${i}`} className="border-t border-slate-100">
                      <Td>{r.createdAt ? r.createdAt.slice(0, 10) : '(이전 형식)'}</Td>
                      <Td>{dash(r.userName)}</Td>
                      <Td mono>{r.salesId}</Td>
                      <Td>{dash(r.projectName)}</Td>
                      <Td><span className="whitespace-pre-wrap break-words">{r.content}</span></Td>
                    </tr>
                  ))}
                </Table>
                {d.memo.total > d.memo.rows.length && <div className="text-xs text-slate-500 mt-1">최근 {d.memo.rows.length}개만 — 나머지는 각 영업건 상세에서</div>}
              </Section>

              <Section title="명절선물" total={d.gift.total}>
                <Table head={['명절', '담당', '받는 분', '품목', '수량', '전달', '완료']}>
                  {d.gift.rows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <Td>{r.year} {r.kind}</Td>
                      <Td>{dash(r.owner)}</Td>
                      <Td>{dash(r.recipient)}</Td>
                      <Td>{dash(r.itemName)}</Td>
                      <Td right>{r.qty ?? '-'}</Td>
                      <Td>{dash(r.method)}</Td>
                      <Td>{r.isDone ? '✓' : '-'}</Td>
                    </tr>
                  ))}
                </Table>
              </Section>

              <Section title="보낸 메일" total={d.mail.total}>
                <Table head={['보낸 날', '보낸 사람', '제목', '받는 주소']}>
                  {d.mail.rows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <Td>{r.createdAt.slice(0, 16).replace('T', ' ')}</Td>
                      <Td>{dash(r.sender)}</Td>
                      <Td>{dash(r.subject)}</Td>
                      <Td><span className="text-xs text-slate-500 break-all">{dash(r.toAddrs)}</span></Td>
                    </tr>
                  ))}
                </Table>
                <div className="text-xs text-slate-500 mt-1">포털에서 보낸 메일 가운데 받는 주소가 이 거래처 담당자 주소록의 메일인 것 — 담당자에 메일이 없으면 안 잡힙니다</div>
              </Section>

              <Section title="첨부 문서" total={d.attachment.total}>
                <Table head={['올린 날', '종류', '파일', '매출번호', '프로젝트']}>
                  {d.attachment.rows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <Td>{r.uploadedAt.slice(0, 10)}</Td>
                      <Td>{dash(r.kind)}</Td>
                      <Td>
                        <button type="button" className="text-blue-700 hover:underline text-left" onClick={() => previewAttachment({ id: r.id, filename: r.filename })} title="미리보기">
                          {r.filename}
                        </button>
                      </Td>
                      <Td mono>{r.salesId}</Td>
                      <Td>{dash(r.projectName)}</Td>
                    </tr>
                  ))}
                </Table>
              </Section>
            </>
          )}
        </div>
    </DraggableModal>
  );
}

/** 하이라이트 칸 — 값 크게 · 아래 한 줄 설명 (v2.318.0) */
function Highlight({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 min-w-0">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-lg font-bold text-slate-900 truncate" title={value}>{value}</div>
      <div className="text-[11px] text-slate-500 truncate" title={sub}>{sub}</div>
    </div>
  );
}

function SummaryCard({ label, n, cls }: { label: string; n: number; cls: string }) {
  return (
    <div className={`border rounded p-3 text-center ${cls}`}>
      <div className="text-2xl font-bold">{n}</div>
      <div className="text-xs mt-0.5">{label} (건)</div>
    </div>
  );
}

function Section({ title, total, children }: { title: string; total: number; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-baseline gap-2 mb-1.5">
        <h3 className="ui-subsection-title">{title}</h3>
        <span className="text-xs text-slate-500">총 {total}건{total > 200 ? ' (상위 200건 표시)' : ''}</span>
      </div>
      {total === 0 ? (
        <div className="text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded p-3 text-center">연결된 {title} 내역 없음</div>
      ) : (
        <div className="border border-slate-200 rounded overflow-x-auto max-h-64 overflow-y-auto">{children}</div>
      )}
    </div>
  );
}

function Table({ head, children }: { head: string[]; children: React.ReactNode }) {
  return (
    <table className="w-full text-xs">
      <thead className="bg-slate-50 sticky top-0">
        <tr>
          {head.map((h) => (
            <th key={h} className="text-left font-semibold text-slate-600 px-2 py-1.5 whitespace-nowrap">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}

function Td({ children, mono, right }: { children: React.ReactNode; mono?: boolean; right?: boolean }) {
  return (
    <td className={`px-2 py-1 text-slate-700 ${mono ? 'font-mono' : ''} ${right ? 'text-right' : ''}`}>
      {children}
    </td>
  );
}
